/**
 * ノベルティ選択（snippets/kl-novelty.liquid）
 *
 * - 数量セレクターの値に合わせて選択枠（1点目 / 2点目 …）を増減
 * - 各枠でサイズ or「ノベルティ不要」の選択が必須。未選択でカート追加するとエラー
 * - 本体とノベルティは line item property「_novelty_group」で紐付ける
 *   （本体削除時のノベルティ削除は assets/cart-store.js の syncNovelties）
 * - カートタイプ「page」（通常POST）のとき、ノベルティを1つ以上選んだ場合のみ submit を横取りし、
 *   /cart/add.js に本体 + ノベルティをまとめて追加してから /cart へ遷移する
 *   （ドロワーカート時は assets/product-form.js から window.klNovelty.collect を使用）
 */
(() => {
  if (window.klNovelty) return;

  const CONTAINER_SELECTOR = '[data-kl-novelty]';
  const SLOT_SELECTOR = '[data-kl-novelty-slot]';
  const INPUT_SELECTOR = '[data-kl-novelty-input]';
  const NONE_VALUE = 'none';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const getQuantity = (form) => {
    const input = form.elements.quantity;
    const quantity = parseInt(input && input.value, 10);
    return quantity > 0 ? quantity : 1;
  };

  // 数量に合わせて選択枠を増減
  const syncSlots = (container, quantity) => {
    const slotsEl = container.querySelector('[data-kl-novelty-slots]');
    const slots = Array.from(slotsEl.querySelectorAll(SLOT_SELECTOR));
    const group = container.dataset.group;

    for (let i = slots.length; i < quantity; i++) {
      const clone = slots[0].cloneNode(true);
      clone.querySelectorAll(INPUT_SELECTOR).forEach((input) => {
        input.name = `kl_novelty_${group}_${i}`;
        input.checked = false;
      });
      slotsEl.appendChild(clone);
      slots.push(clone);
    }

    slots.slice(quantity).forEach((slot) => slot.remove());

    slots.slice(0, quantity).forEach((slot, i) => {
      const label = slot.querySelector('[data-kl-novelty-slot-label]');
      label.textContent = `${i + 1}点目`;
      label.hidden = quantity < 2;
    });
  };

  const showError = (form, message) => {
    const container = form.querySelector(CONTAINER_SELECTOR);
    if (!container) return;
    const errorEl = container.querySelector('[data-kl-novelty-error]');
    errorEl.textContent = message;
    errorEl.hidden = false;
    container.classList.add('is-error');
    container.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const clearError = (form) => {
    const container = form && form.querySelector(CONTAINER_SELECTOR);
    if (!container) return;
    container.querySelector('[data-kl-novelty-error]').hidden = true;
    container.classList.remove('is-error');
  };

  /**
   * フォームからノベルティの選択内容を取得
   * @returns {{ error?: string, group: string|null, items: Array }}
   *   items は /cart/add.js 形式（properties はオブジェクト）
   */
  const collect = (form) => {
    const container = form.querySelector(CONTAINER_SELECTOR);
    if (!container) return { group: null, items: [] };

    const quantity = getQuantity(form);
    syncSlots(container, quantity);

    const variantIds = [];
    let missing = false;

    container.querySelectorAll(SLOT_SELECTOR).forEach((slot) => {
      const checked = slot.querySelector(`${INPUT_SELECTOR}:checked`);
      if (!checked) {
        missing = true;
      } else if (checked.value !== NONE_VALUE) {
        variantIds.push(checked.value);
      }
    });

    if (missing) {
      return {
        error:
          quantity > 1
            ? `ノベルティを${quantity}点分選択してください。不要な場合は「ノベルティ不要」を選択してください。`
            : 'ノベルティを選択してください。不要な場合は「ノベルティ不要」を選択してください。',
        group: null,
        items: [],
      };
    }

    if (!variantIds.length) return { group: null, items: [] };

    const group = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const counts = variantIds.reduce((acc, id) => {
      acc[id] = (acc[id] || 0) + 1;
      return acc;
    }, {});

    const items = Object.entries(counts).map(([id, quantity]) => ({
      id: Number(id),
      quantity,
      properties: { _novelty: '1', _novelty_group: group },
    }));

    return { group, items };
  };

  window.klNovelty = { collect, showError, clearError };

  // 初期表示・数量変更時に選択枠を同期
  const refreshAll = () => {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      const form = container.closest('form');
      if (form) syncSlots(container, getQuantity(form));
    });
  };

  refreshAll();

  document.addEventListener('click', (e) => {
    if (e.target.closest('quantity-input button')) setTimeout(refreshAll);
  });

  document.addEventListener('change', (e) => {
    const target = e.target;
    if (target.name === 'quantity') setTimeout(refreshAll);
    if (target.matches && target.matches(INPUT_SELECTOR)) clearError(target.closest('form'));
  });

  // カートタイプ「page」用
  const postItems = async (items) => {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Error');
    return json;
  };

  const buildMainItem = (form) => {
    const item = {};
    const properties = {};

    for (const [key, value] of new FormData(form).entries()) {
      if (key.startsWith('kl_novelty') || key === 'form_type' || key === 'utf8') continue;

      const match = key.match(/^properties\[(.+)\]$/);
      if (match) {
        if (value !== '') properties[match[1]] = value;
      } else {
        item[key] = value;
      }
    }

    item.properties = properties;
    if (!item.quantity) item.quantity = 1;
    return item;
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector(CONTAINER_SELECTOR)) return;
    if (form.closest('product-form')) return; // ドロワーカートは product-form.js で処理

    const result = collect(form);

    if (result.error) {
      e.preventDefault();
      showError(form, result.error);
      return;
    }

    clearError(form);

    if (!result.items.length) return; // すべて「ノベルティ不要」なら通常の送信

    e.preventDefault();

    const button = form.querySelector('[name="add"]');
    if (button) button.disabled = true;

    const mainItem = buildMainItem(form);
    mainItem.properties._novelty_group = result.group;

    try {
      // 本体 + ノベルティを一括追加（どれか1つでも追加できない場合は全て追加されない）
      await postItems([mainItem, ...result.items]);
    } catch (err) {
      if (button) button.disabled = false;
      showError(form, err.message);
      return;
    }

    window.location.href = `${root}cart`;
  });
})();
